import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { streamAI } from "@/lib/stream";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Send, Search, Trash2, Globe } from "lucide-react";

const PRESETS = [
  "heritage cattle stud breeders contact email",
  "rare breed sheep breeders contact",
  "heritage pig breeders club contact",
  "rare breed horse stud contact email",
  "heritage goat breeders association contact",
  "rare poultry breeders club contact",
  "camel breeders association contact email",
  "rare dog breed breeders club contact",
  "artificial insemination services cattle veterinary contact email",
  "equine artificial insemination stud services veterinarian contact",
  "livestock embryo transfer veterinary services contact email",
  "cryogenic semen embryo transport logistics livestock contact",
  "livestock semen collection freezing storage services contact",
];

export default function DiscoveryPanel() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState({});
  const [searching, setSearching] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [queue, setQueue] = useState([]);
  const [source, setSource] = useState("");
  const [draft, setDraft] = useState(null); // { id, name, email, text, streaming }

  const loadQueue = () => api.get("/discovery/queue").then(r => setQueue(r.data.candidates)).catch(() => {});
  useEffect(() => { loadQueue(); }, []);

  const runSearch = async (q) => {
    const qt = (q || query).trim();
    if (!qt) return;
    setSearching(true);
    try {
      const { data } = await api.post("/discovery/search", { query: qt });
      setResults(data.results);
      setSelected({});
      setSource(data.source || "");
      if (!data.results.length && data.source === "none") toast.error("Search temporarily rate-limited — wait a minute and retry");
      else if (!data.results.length) toast.info("No results — try a different query");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSearching(false); }
  };

  const extract = async () => {
    const urls = results.filter((_, i) => selected[i]).map(r => r.url).slice(0, 5);
    if (!urls.length) { toast.error("Select at least one result"); return; }
    setExtracting(true);
    try {
      const { data } = await api.post("/discovery/extract", { urls });
      const added = data.results.filter(r => r.status === "added").length;
      const known = data.results.filter(r => r.status === "duplicate" || r.status === "already_in_directory").length;
      const none = data.results.filter(r => r.status === "no_email").length;
      const errs = data.results.filter(r => r.status.startsWith("error")).length;
      toast.success(`Added ${added} candidate(s)${known ? ` · ${known} already known` : ""}${none ? ` · ${none} without email` : ""}${errs ? ` · ${errs} failed to load` : ""}`);
      loadQueue();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExtracting(false); }
  };

  const draftIntro = async (c) => {
    setDraft({ id: c.id, name: c.org_name, email: c.email, text: "", streaming: true });
    try {
      await streamAI(`/discovery/${c.id}/draft`, { method: "POST", onDelta: (t) => setDraft(d => (d ? { ...d, text: d.text + t } : d)) });
      setDraft(d => (d ? { ...d, streaming: false } : d));
      loadQueue();
    } catch (e) { setDraft(null); toast.error(formatApiError(e)); }
  };

  const sendIntro = async () => {
    try {
      await api.post(`/discovery/${draft.id}/send`);
      toast.success(`Intro email sent to ${draft.email}`);
      setDraft(null);
      loadQueue();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setStage = async (c, stage) => {
    try { await api.post(`/discovery/${c.id}/stage`, { stage }); loadQueue(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (c) => {
    try { await api.delete(`/discovery/${c.id}`); toast.success("Removed from queue"); loadQueue(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="surface-card p-5" data-testid="discovery-search-card">
        <div className="heading font-semibold">Discover breeder contacts on the web</div>
        <div className="text-xs text-stone-500 mt-0.5">AI searches the open web for breeder/stud contact details, extracts emails, and drafts intro emails — nothing is sent without your review.</div>
        <div className="flex gap-2 mt-3">
          <Input data-testid="discovery-search-input" placeholder="e.g. heritage cattle stud breeders contact email" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} />
          <Button type="button" data-testid="discovery-search-btn" className="btn-forest" disabled={searching} onClick={() => runSearch()}>
            <Search className="h-4 w-4 mr-1" /> {searching ? "Searching…" : "Search"}
          </Button>
        </div>
        <div className="flex gap-1.5 flex-wrap mt-2">
          {PRESETS.map(p => <button key={p} type="button" data-testid={`discovery-preset-${p.split(" ")[1]}`} className="text-[11px] mono px-2 py-1 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-600" onClick={() => { setQuery(p); runSearch(p); }}>{p}</button>)}
        </div>
        {results.length > 0 && (
          <div className="mt-3 border border-stone-200 rounded-md overflow-hidden" data-testid="discovery-results">
            {results.map((r, i) => (
              <label key={i} className="flex items-start gap-2 px-3 py-2 border-b border-stone-100 text-sm cursor-pointer hover:bg-stone-50" data-testid={`discovery-result-${i}`}>
                <input type="checkbox" className="mt-1" checked={!!selected[i]} onChange={(e) => setSelected({ ...selected, [i]: e.target.checked })} />
                <div className="flex-1">
                  <div className="font-medium">{r.title}</div>
                  <div className="text-xs text-stone-500 break-all">{r.url}</div>
                  {r.snippet && <div className="text-xs text-stone-400 mt-0.5">{r.snippet.slice(0, 140)}</div>}
                </div>
              </label>
            ))}
            <div className="px-3 py-2 bg-stone-50 flex items-center justify-between">
              {source && <span className="text-[10px] mono uppercase text-stone-400" data-testid="discovery-source">via {source}</span>}
              <Button type="button" size="sm" data-testid="discovery-extract-btn" className="btn-earth" disabled={extracting} onClick={extract}>
                {extracting ? "Extracting…" : `Extract contacts (${Object.values(selected).filter(Boolean).length})`}
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="surface-card overflow-hidden" data-testid="discovery-queue">
        <table className="data-table">
          <thead><tr><th>Candidate</th><th>Species</th><th>Stage</th><th></th></tr></thead>
          <tbody>
            {queue.map(c => (
              <tr key={c.id} data-testid={`discovery-row-${c.id}`}>
                <td>
                  <div className="font-medium">{c.org_name || "Unknown"}</div>
                  <div className="text-xs text-stone-500 flex items-center gap-1 flex-wrap">
                    <Globe className="h-3 w-3" /> {c.country || "—"} · <span className="mono">{c.email}</span>
                    {c.category === "vet_ai" && <span className="px-1.5 py-0.5 rounded-full pill-stable text-[10px]">AI vet</span>}
                    {c.category === "cryo_transport" && <span className="px-1.5 py-0.5 rounded-full pill-stable text-[10px]">cryo/transport</span>}
                    {c.category === "association" && <span className="px-1.5 py-0.5 rounded-full pill-watch text-[10px]">association</span>}
                  </div>
                  <a href={c.source_url} target="_blank" rel="noreferrer" className="text-[10px] text-stone-400 underline break-all">{c.source_url}</a>
                </td>
                <td className="text-xs capitalize">{c.species || "—"}</td>
                <td>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] mono uppercase px-1.5 py-0.5 rounded-full pill-stable" data-testid={`discovery-stage-${c.id}`}>{c.stage}</span>
                    <Select value={c.stage} onValueChange={(v) => setStage(c, v)}>
                      <SelectTrigger className="h-7 w-28 text-xs" data-testid={`discovery-stage-select-${c.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{["new", "drafted", "sent", "replied", "partnered", "declined"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </td>
                <td className="flex gap-2">
                  <Button type="button" size="sm" variant="outline" data-testid={`discovery-draft-${c.id}`} onClick={() => draftIntro(c)}>
                    <Sparkles className="h-3.5 w-3.5 mr-1" /> {c.draft ? "Re-draft" : "Draft with Claude"}
                  </Button>
                  <Button type="button" size="sm" variant="outline" data-testid={`discovery-delete-${c.id}`} onClick={() => remove(c)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
            {queue.length === 0 && <tr><td colSpan={4} className="text-stone-500 text-center py-6">No discovered candidates yet — run a search above.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!draft} onOpenChange={(o) => !o && !draft?.streaming && setDraft(null)}>
        <DialogContent data-testid="discovery-draft-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[color:var(--earth-amber)]" /> Intro draft — {draft?.name}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Review the AI-drafted introduction email before sending.</DialogDescription>
          <div data-testid="discovery-draft-text" className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed max-h-[50vh] overflow-y-auto border border-stone-200 rounded-md p-4 bg-stone-50">
            {draft?.text}{draft?.streaming && <span className="inline-block w-2 h-4 bg-[color:var(--earth-amber)] animate-pulse" />}
          </div>
          <div className="flex items-center justify-between">
            <div className="text-[10px] mono uppercase text-stone-400">Claude Sonnet 5 · review before sending</div>
            {draft?.email ? (
              <Button data-testid="discovery-send-btn" className="btn-forest" disabled={!draft || draft.streaming || !draft.text} onClick={sendIntro}>
                <Send className="h-3.5 w-3.5 mr-1" /> Send to {draft.email}
              </Button>
            ) : <span className="text-xs text-amber-700">No contact email</span>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
